
'use client';

import { useState, useRef, MouseEvent, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { ZoomIn, ZoomOut, Download, X, RefreshCw, ChevronLeft, ChevronRight, Play, Pause, Maximize2, Volume2, VolumeX, FastForward, Rewind, Gauge, RotateCw, LayoutGrid, DownloadCloud } from 'lucide-react';
import { cn } from '@/doi-tac-module/lib/utils';

export interface MediaItem {
  src: string;
  alt: string;
  type?: 'image' | 'video';
  candidateName?: string;
  candidateId?: string;
}

interface MediaViewerProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  items?: MediaItem[];
  currentIndex?: number;
  onIndexChange?: (index: number) => void;
  // Backward compatibility
  images?: MediaItem[];
  src?: string;
  alt?: string;
}

export function MediaViewer({ 
  isOpen, 
  onOpenChange, 
  items = [], 
  currentIndex = 0, 
  onIndexChange,
  images = [],
  src,
  alt
}: MediaViewerProps) {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(currentIndex);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastMousePosition = useRef({ x: 0, y: 0 });
  const touchStart = useRef<{ x: number, y: number } | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoProgress, setVideoProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [feedback, setFeedback] = useState<{ type: 'play' | 'pause' | 'ff' | 'rw' | 'rotate', visible: boolean }>({ type: 'play', visible: false });
  const feedbackTimeout = useRef<NodeJS.Timeout | null>(null);
  const [rotation, setRotation] = useState(0);
  const [touchDistance, setTouchDistance] = useState<number | null>(null);
  const [showThumbnails, setShowThumbnails] = useState(false);

  const [isMuted, setIsMuted] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('video-muted') === 'true';
    }
    return false;
  });

  useEffect(() => {
    localStorage.setItem('video-muted', isMuted.toString());
  }, [isMuted]);

  // Handle backward compatibility for items list
  const effectiveItems = useMemo(() => {
    if (items && items.length > 0) return items;
    if (images && images.length > 0) return images;
    if (src) return [{ src, alt: alt || '', type: 'image' as const }];
    return [];
  }, [items, images, src, alt]);

  // Sync internal index with prop index
  useEffect(() => {
    if (isOpen) {
      setCurrentIdx(currentIndex || 0);
    }
  }, [currentIndex, isOpen]);

  const currentItem = effectiveItems[currentIdx] || { src: '', alt: '', type: 'image' };
  const isVideo = currentItem.type === 'video' || currentItem.src.toLowerCase().endsWith('.mp4');

  const showFeedback = (type: 'play' | 'pause' | 'ff' | 'rw' | 'rotate') => {
    setFeedback({ type, visible: true });
    if (feedbackTimeout.current) clearTimeout(feedbackTimeout.current);
    feedbackTimeout.current = setTimeout(() => {
      setFeedback(f => ({ ...f, visible: false }));
    }, 600);
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        showFeedback('pause');
      } else {
        videoRef.current.play();
        showFeedback('play');
      }
      setIsPlaying(!isPlaying);
    }
  };

  const seek = (seconds: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime += seconds;
      showFeedback(seconds > 0 ? 'ff' : 'rw');
    }
  };

  const handleZoomIn = () => setScale(s => Math.min(s + 0.3, 5));
  const handleZoomOut = () => {
    const newScale = Math.max(scale - 0.3, 1);
    if (newScale === 1) {
      setPosition({ x: 0, y: 0 });
    }
    setScale(newScale);
  };
  const handleRotate = () => {
    setRotation(r => (r + 90) % 360);
    showFeedback('rotate');
  };
  const handleReset = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setRotation(0);
  };

  const downloadAll = async () => {
    const downloadSequence = async () => {
      for (let i = 0; i < effectiveItems.length; i++) {
        const item = effectiveItems[i];
        const link = document.createElement('a');
        link.href = item.src;
        link.download = item.alt.replace(/\s/g, '_') || `file_${i}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        // Small delay to prevent browser browser from blocking multiple downloads
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    };
    downloadSequence();
  };

  const handlePrev = () => {
    if (currentIdx > 0) {
      setCurrentIdx(currentIdx - 1);
      onIndexChange?.(currentIdx - 1);
      handleReset();
    }
  };

  const handleNext = () => {
    if (currentIdx < effectiveItems.length - 1) {
      setCurrentIdx(currentIdx + 1);
      onIndexChange?.(currentIdx + 1);
      handleReset();
    }
  };

  const handleMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    if (!isVideo && scale > 1) {
      e.preventDefault();
      setIsDragging(true);
      lastMousePosition.current = { x: e.clientX, y: e.clientY };
    }
  };

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (!isVideo && isDragging && scale > 1) {
      e.preventDefault();
      const dx = e.clientX - lastMousePosition.current.x;
      const dy = e.clientY - lastMousePosition.current.y;
      lastMousePosition.current = { x: e.clientX, y: e.clientY };

      setPosition(p => ({ x: p.x + dx, y: p.y + dy }));
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };
  
  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (isVideo) {
      if (Math.abs(e.deltaY) > 30) {
        if (e.deltaY > 0) handleNext();
        else handlePrev();
      }
      return;
    }
    if (e.deltaY < 0) {
      setScale(s => Math.min(s + 0.05, 5));
    } else {
      const newScale = Math.max(scale - 0.05, 1);
      if (newScale === 1) setPosition({ x: 0, y: 0 });
      setScale(newScale);
    }
  };

  const lastTap = useRef<number>(0);

  const handleTouchStart = (e: React.TouchEvent) => {
    const now = Date.now();
    const touch = e.touches[0];
    
    if (isVideo && now - lastTap.current < 300) {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = touch.clientX - rect.left;
      if (x < rect.width / 2) seek(-10);
      else seek(10);
      lastTap.current = 0;
      return;
    }

    lastTap.current = now;
    touchStart.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    // Pinch to zoom logic
    if (e.touches.length === 2 && !isVideo) {
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      if (touchDistance) {
        const delta = dist - touchDistance;
        setScale(s => Math.min(Math.max(s + delta * 0.01, 1), 5));
      }
      setTouchDistance(dist);
      return;
    }

    if (!touchStart.current) return;
    const touch = e.touches[0];
    const dx = touch.clientX - touchStart.current.x;
    const dy = touch.clientY - touchStart.current.y;

    // Detect vertical swipe for TikTok feel (only if not zoomed in)
    if (scale === 1 && Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 50) {
      if (dy < 0) handleNext();
      else handlePrev();
      touchStart.current = null;
    } else if (scale > 1) {
      // Panning while zoomed
      setPosition(p => ({ x: p.x + dx * 0.5, y: p.y + dy * 0.5 }));
      touchStart.current = { x: touch.clientX, y: touch.clientY };
    }
  };

  const handleTouchEnd = () => {
    touchStart.current = null;
    setTouchDistance(null);
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'ArrowLeft') {
        if (isVideo) seek(-5);
        else handlePrev();
      }
      if (e.key === 'ArrowRight') {
        if (isVideo) seek(5);
        else handleNext();
      }
      if (e.key === 'ArrowUp' && isVideo) handlePrev();
      if (e.key === 'ArrowDown' && isVideo) handleNext();
      if (e.key === ' ') {
        e.preventDefault();
        if (isVideo) togglePlay();
      }
      if (isVideo) {
        if (e.key.toLowerCase() === 'j') seek(-10);
        if (e.key.toLowerCase() === 'l') seek(10);
        if (e.key.toLowerCase() === 'k') togglePlay();
      }
      if (e.key === 'Escape') onOpenChange(false);
      if (!isVideo) {
        if (e.key === '+' || e.key === '=') handleZoomIn();
        if (e.key === '-') handleZoomOut();
        if (e.key.toLowerCase() === 'r') handleRotate();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIdx, scale, isVideo, isPlaying]);

  // Handle Playback Rate
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate, currentIdx]);

  // Reset state when the dialog is closed
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        handleReset();
        setIsDragging(false);
      }, 300);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-[100vw] w-screen h-screen p-0 bg-white/95 border-none flex flex-col items-center justify-center outline-none overflow-hidden"
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        hideCloseButton={true}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{currentItem.alt}</DialogTitle>
          <DialogDescription>Trình xem media tích hợp cao cấp</DialogDescription>
        </DialogHeader>

        {/* Top Navbar */}
        <div className="absolute top-0 left-0 right-0 h-20 bg-gradient-to-b from-white/90 to-transparent z-50 flex items-center justify-between px-8">
          <div className="flex flex-col">
            <h3 className="text-slate-900 font-headline font-bold text-xl flex items-center gap-2">
              {isVideo ? <Play className="h-5 w-5 text-primary fill-primary" /> : <Maximize2 className="h-5 w-5 text-primary" />}
              {currentItem.alt}
            </h3>
            <p className="text-slate-500 text-xs font-medium tracking-widest uppercase mt-1">
              Document {currentIdx + 1} of {effectiveItems.length}
            </p>
          </div>
          <Button 
            variant="ghost" size="icon" 
            className="text-slate-600 hover:bg-slate-100 hover:text-slate-900 rounded-full h-12 w-12 transition-all hover:rotate-90 duration-300" 
            onClick={() => onOpenChange(false)}
          >
            <X className="h-8 w-8" />
          </Button>
        </div>

        {/* Main Content Viewport */}
        <div 
          className="relative w-full h-full flex items-center justify-center overflow-hidden"
          onWheel={handleWheel}
        >
          {/* Side Navigation */}
          {effectiveItems.length > 1 && (
            <>
              <div className="absolute left-0 top-0 bottom-0 flex items-center px-4 z-40">
                <Button
                  variant="ghost" size="icon"
                  className={cn(
                    "bg-slate-200/50 hover:bg-slate-300/50 backdrop-blur-md text-slate-500 hover:text-slate-900 rounded-full h-16 w-16 transition-all",
                    currentIdx === 0 && "opacity-0 pointer-events-none"
                  )}
                  onClick={handlePrev}
                >
                  <ChevronLeft className="h-10 w-10" />
                </Button>
              </div>
              <div className="absolute right-0 top-0 bottom-0 flex items-center px-4 z-40">
                <Button
                  variant="ghost" size="icon"
                  className={cn(
                    "bg-slate-200/50 hover:bg-slate-300/50 backdrop-blur-md text-slate-500 hover:text-slate-900 rounded-full h-16 w-16 transition-all",
                    currentIdx === effectiveItems.length - 1 && "opacity-0 pointer-events-none"
                  )}
                  onClick={handleNext}
                >
                  <ChevronRight className="h-10 w-10" />
                </Button>
              </div>
            </>
          )}

          <div
            ref={containerRef}
            className={cn(
                "absolute transition-all duration-300 ease-out flex items-center justify-center", 
                isDragging ? "cursor-grabbing duration-0" : isVideo ? "" : "cursor-grab"
            )}
            style={{ 
                left: '50%', 
                top: '50%',
                transform: `translate(-50%, -50%) translate(${position.x}px, ${position.y}px) scale(${scale}) rotate(${rotation}deg)`,
            }}
            onMouseDown={handleMouseDown}
          >
            {currentItem.src && (
              isVideo ? (
                <div 
                  className="relative flex flex-col items-center justify-center animate-in fade-in zoom-in duration-500 group"
                  onClick={togglePlay}
                >
                    <video 
                        ref={videoRef}
                        src={currentItem.src}
                        className="max-w-[90vw] max-h-[85vh] w-auto h-auto block shadow-2xl shadow-slate-200 rounded-md outline-none cursor-pointer"
                        controls={false}
                        autoPlay
                        loop
                        muted={isMuted}
                        playsInline
                        onPlay={() => setIsPlaying(true)}
                        onPause={() => setIsPlaying(false)}
                        onTimeUpdate={(e) => {
                          const v = e.currentTarget;
                          setVideoProgress((v.currentTime / v.duration) * 100);
                        }}
                    />

                    {/* Interaction Feedback Icon */}
                    {feedback.visible && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-50 animate-out fade-out zoom-out duration-700">
                        <div className="bg-white/40 backdrop-blur-md rounded-full p-8 text-slate-800 scale-150 border border-slate-200 shadow-2xl">
                          {feedback.type === 'play' && <Play className="h-12 w-12 fill-current" />}
                          {feedback.type === 'pause' && <Pause className="h-12 w-12 fill-current" />}
                          {feedback.type === 'ff' && <FastForward className="h-12 w-12 fill-current" />}
                          {feedback.type === 'rw' && <Rewind className="h-12 w-12 fill-current" />}
                          {feedback.type === 'rotate' && <RotateCw className="h-12 w-12" />}
                        </div>
                      </div>
                    )}

                    {/* TikTok Style Metadata Overlay */}
                    <div className="absolute bottom-6 left-6 text-slate-900 text-left pointer-events-none transition-opacity group-hover:opacity-100 z-40">
                        <div className="space-y-0.5">
                            <h4 className="font-bold text-lg md:text-xl flex items-center gap-2">
                              {currentItem.candidateName} 
                              <span className="text-slate-400 text-xs font-mono font-normal">#{currentItem.candidateId}</span>
                            </h4>
                            <p className="text-slate-600 text-sm font-medium flex items-center gap-1.5">
                              <Play className="h-3 w-3 fill-current" /> {currentItem.alt}
                            </p>
                        </div>
                    </div>

                    {/* TikTok Style Progress Bar */}
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-slate-200/50 group-hover:h-1.5 transition-all">
                      <div 
                        className="h-full bg-primary transition-all duration-100 ease-linear shadow-[0_0_10px_rgba(25,166,223,0.3)]"
                        style={{ width: `${videoProgress}%` }}
                      />
                    </div>
                </div>
              ) : (
                  <Image
                    src={currentItem.src}
                    alt={currentItem.alt}
                    width={1600}
                    height={1200}
                    className="max-w-none select-none drop-shadow-2xl animate-in fade-in zoom-in duration-500"
                    style={{ 
                      maxHeight: '85vh', 
                      maxWidth: '85vw', 
                      width: 'auto', 
                      height: 'auto', 
                      objectFit: 'contain' 
                    }}
                    draggable="false"
                    priority
                  />
              )
            )}
          </div>
        </div>

        {/* Global Controls Overlay */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-8 duration-500 delay-150">
           <div className="bg-white/90 backdrop-blur-xl rounded-3xl p-3 flex items-center gap-5 border border-slate-200 shadow-2xl ring-1 ring-slate-200/5">
                {!isVideo && (
                    <>
                        <div className="flex items-center gap-1 border-r border-slate-200 pr-5">
                            <Button variant="ghost" size="icon" className="text-slate-600 hover:bg-slate-100 hover:text-slate-900 h-10 w-10 rounded-xl" onClick={handleZoomOut} disabled={scale <= 1}>
                                <ZoomOut className="h-5 w-5" />
                            </Button>
                            <div className="text-slate-900 text-sm font-mono min-w-[3.5rem] text-center font-bold">
                                {Math.round(scale * 100)}%
                            </div>
                            <Button variant="ghost" size="icon" className="text-slate-600 hover:bg-slate-100 hover:text-slate-900 h-10 w-10 rounded-xl" onClick={handleZoomIn}>
                                <ZoomIn className="h-5 w-5" />
                            </Button>
                        </div>
                        
                        <div className="flex items-center gap-1 border-r border-slate-200 pr-5">
                            <Button variant="ghost" size="icon" className="text-slate-600 hover:bg-slate-100 hover:text-slate-900 h-10 w-10 rounded-xl" onClick={handleReset} disabled={scale === 1 && position.x === 0 && position.y === 0}>
                                <RefreshCw className="h-5 w-5" />
                            </Button>
                        </div>
                    </>
                )}

                {isVideo && (
                  <div className="flex items-center gap-1 border-r border-slate-200 pr-5">
                    {[0.5, 1, 1.5, 2].map(speed => (
                      <Button 
                        key={speed}
                        variant="ghost" 
                        className={cn(
                          "h-10 px-2 rounded-xl text-[10px] font-bold transition-all",
                          playbackRate === speed ? "bg-primary text-white scale-110 shadow-lg" : "text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                        )}
                        onClick={() => setPlaybackRate(speed)}
                      >
                        {speed}x
                      </Button>
                    ))}
                  </div>
                )}

                <Button variant="ghost" size="icon" className="text-slate-600 hover:bg-slate-100 hover:text-slate-900 h-10 w-10 rounded-xl" asChild>
                <a href={currentItem.src} download={`${currentItem.alt.replace(/\s/g, '_')}`}>
                    <Download className="h-5 w-5" />
                </a>
                </Button>

                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="text-slate-600 hover:bg-slate-100 hover:text-slate-900 h-10 w-10 rounded-xl" 
                  onClick={downloadAll}
                  title="Tải tất cả"
                >
                    <DownloadCloud className="h-5 w-5" />
                </Button>

                {!isVideo && (
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="text-slate-600 hover:bg-slate-100 hover:text-slate-900 h-10 w-10 rounded-xl" 
                    onClick={handleRotate}
                    title="Xoay ảnh"
                  >
                      <RotateCw className="h-5 w-5" />
                  </Button>
                )}

                {isVideo && (
                    <Button 
                      variant="ghost" size="icon" 
                      className="text-slate-600 hover:bg-slate-100 hover:text-slate-900 h-10 w-10 rounded-xl"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsMuted(!isMuted);
                      }}
                    >
                      {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
                    </Button>
                )}
           </div>
        </div>

        {/* Thumbnail Strip */}
        <div className={cn(
          "absolute bottom-0 left-0 right-0 h-24 bg-white/80 backdrop-blur-md border-t border-slate-200 flex items-center gap-3 px-6 overflow-x-auto transition-transform duration-300 z-[60] scrollbar-hide",
          !showThumbnails && "translate-y-full"
        )}>
           {effectiveItems.map((item, idx) => (
             <div 
               key={idx}
               className={cn(
                 "relative h-16 w-16 min-w-[4rem] rounded-lg overflow-hidden cursor-pointer transition-all duration-300",
                 currentIdx === idx ? "ring-2 ring-primary scale-110" : "opacity-40 hover:opacity-80 scale-90"
               )}
               onClick={() => {
                 setCurrentIdx(idx);
                 onIndexChange?.(idx);
                 handleReset();
               }}
             >
                <Image src={item.src} alt={item.alt} fill className="object-cover" />
                {item.type === 'video' && <div className="absolute inset-0 flex items-center justify-center bg-white/20"><Play className="h-4 w-4 text-primary fill-primary"/></div>}
             </div>
           ))}
        </div>

        {/* Thumbnail Toggle */}
        <div className="absolute right-8 bottom-32 z-50">
           <Button 
             variant="ghost" size="icon" 
             className={cn("text-slate-500 hover:bg-slate-100 hover:text-slate-900 rounded-full bg-white/40 backdrop-blur-xl border border-slate-200", showThumbnails && "text-primary bg-primary/10")} 
             onClick={() => setShowThumbnails(!showThumbnails)}
           >
             <LayoutGrid className="h-6 w-6" />
           </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
